import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: 8.5rem 1.5rem 4.25rem;
  background: linear-gradient(180deg, #fbfbfd 0%, #fbfbfd 78%, #ffffff 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(circle at 50% 18%, rgba(0, 113, 227, 0.09), transparent 31rem),
      radial-gradient(circle at 72% 42%, rgba(90, 200, 250, 0.08), transparent 24rem);
  }

  @media (max-width: 700px) {
    padding: 7rem 1rem 3.25rem;
  }
`;

const HeroContent = styled.div`
  position: relative;
  width: min(1080px, 100%);
  margin: 0 auto;
  text-align: center;
`;

const Eyebrow = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1.65rem;
  padding: 0.42rem 0.72rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.72rem;
  font-weight: 750;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.theme.colors.primary};
  }
`;

const ProjectName = styled(motion.div)`
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.84rem;
  font-weight: 650;
  letter-spacing: 0.16em;
`;

const Title = styled(motion.h1)`
  max-width: 1020px;
  margin: 0 auto;
  color: ${props => props.theme.colors.textPrimary};
  font-size: clamp(2.15rem, 5vw, 4.35rem);
  font-weight: 700;
  letter-spacing: -0.055em;
  line-height: 0.96;

  em {
    background: linear-gradient(90deg, #0071e3, #5e5ce6);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-style: normal;
  }
`;

const Summary = styled(motion.p)`
  max-width: 790px;
  margin: 1.8rem auto 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: clamp(1rem, 1.7vw, 1.18rem);
  line-height: 1.7;
`;

const AuthorInfo = styled(motion.div)`
  max-width: 940px;
  margin: 2.1rem auto 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.82rem;
  line-height: 1.65;

  .authors {
    color: ${props => props.theme.colors.text};
    font-size: 0.9rem;
    font-weight: 600;
  }

  .affiliations {
    margin-top: 0.45rem;
  }

  sup {
    color: ${props => props.theme.colors.secondary};
    font-size: 0.68em;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.45rem;
  margin-top: 1.8rem;
`;

const Button = styled.a<{ $primary?: boolean }>`
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.38rem;
  padding: 0.5rem 0.72rem;
  border: 1px solid ${props => props.$primary ? 'rgba(0, 113, 227, 0.22)' : 'rgba(0, 0, 0, 0.055)'};
  border-radius: 0.7rem;
  background: ${props => props.$primary ? 'rgba(0, 113, 227, 0.055)' : 'rgba(255, 255, 255, 0.68)'};
  color: ${props => props.$primary ? props.theme.colors.primary : props.theme.colors.textPrimary};
  font-size: 0.72rem;
  font-weight: 650;
  box-shadow: none;
  transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;

  &:hover {
    border-color: rgba(0, 113, 227, 0.22);
    background: ${props => props.$primary ? 'rgba(0, 113, 227, 0.08)' : 'rgba(255, 255, 255, 0.96)'};
    color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  }
`;

const Stats = styled(motion.div)`
  max-width: 720px;
  margin: 2.25rem auto 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.45rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Stat = styled.div`
  padding: 0.78rem 0.65rem;
  border: 1px solid rgba(0, 0, 0, 0.045);
  border-radius: 0.82rem;
  background: rgba(255, 255, 255, 0.58);
`;

const StatNumber = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-family: ${props => props.theme.fonts.display};
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1;
`;

const StatLabel = styled.div`
  margin-top: 0.3rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.6rem;
  font-weight: 650;
  letter-spacing: 0.055em;
  text-transform: uppercase;
`;

export const Hero: React.FC = () => (
  <HeroSection id="top">
    <HeroContent>
      <Eyebrow initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span /> Accepted by EMNLP 2026 · Findings
      </Eyebrow>

      <ProjectName initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.08 }}>
        ADVCLI
      </ProjectName>

      <Title initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        Your Harness is Not Secure:<br />
        <em>Benchmarking Real-world Threat</em> of Command Line Interface Agent
      </Title>

      <Summary initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}>
        An MITRE ATT&amp;CK-aligned benchmark for measuring OS-level misuse risk in CLI agents,
        with deterministic verification in a controlled, resettable multi-host sandbox.
      </Summary>

      <AuthorInfo initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }}>
        <div className="authors">
          Weidi Luo<sup>1*</sup>, Qiming Zhang<sup>2*</sup>, Tianyu Lu<sup>3*</sup>, Xiaogeng Liu<sup>3</sup>, Bin Hu<sup>4</sup>, Hung-Chun Chiu<sup>5</sup>,<br />
          Siyuan Ma<sup>6</sup>, Yizhe Zhang<sup>7</sup>, Xusheng Xiao<sup>8</sup>, Yinzhi Cao<sup>3</sup>, Zhen Xiang<sup>1</sup>, Chaowei Xiao<sup>3</sup>
        </div>
        <div className="affiliations">
          <sup>1</sup> University of Georgia · <sup>2</sup> University of South Florida · <sup>3</sup> Johns Hopkins University · <sup>4</sup> University of Maryland, College Park<br />
          <sup>5</sup> Hong Kong University of Science and Technology · <sup>6</sup> Chinese University of Hong Kong · <sup>7</sup> Apple · <sup>8</sup> Arizona State University
        </div>
      </AuthorInfo>

      <ButtonGroup initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Button $primary href="https://arxiv.org/pdf/2510.06607v3" target="_blank" rel="noopener noreferrer">Paper <span aria-hidden>↗</span></Button>
        <Button href="https://github.com/SaFo-Lab/AdvCLI" target="_blank" rel="noopener noreferrer">Code <span aria-hidden>↗</span></Button>
        <Button href="#video-showcase">Video demo <span aria-hidden>↓</span></Button>
        <Button href="#results">Main results <span aria-hidden>↓</span></Button>
      </ButtonGroup>

      <Stats initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Stat><StatNumber>140</StatNumber><StatLabel>evaluated tasks</StatLabel></Stat>
        <Stat><StatNumber>77</StatNumber><StatLabel>ATT&amp;CK techniques</StatLabel></Stat>
        <Stat><StatNumber>7</StatNumber><StatLabel>CLI agents</StatLabel></Stat>
        <Stat><StatNumber>9</StatNumber><StatLabel>foundation models</StatLabel></Stat>
      </Stats>
    </HeroContent>
  </HeroSection>
);
